import { Component, Input, OnInit } from '@angular/core';
import {
    BookingType, CurrencyUnitTotalType, FareComponentInBookingType,
    KeywordType, PriceInBookingType, PriceItemType,
    TravellerType, WhenLastUpdated
} from "@afidium/ts-facade";
import {
    PricesTableService
} from "libraries/component/booking-information/components/booking-price-card/services/prices-table.service";
import {
    ECostOrRetail
} from "libraries/component/booking-information/components/booking-price-card/booking-price-card.enum";
import { wenji } from "libraries/interfaces/IWenji";
import { Yii } from "../../../../../../interfaces/Yii";
import { ComputeTravellerTitlePipe } from "../../pipes/compute-traveller-title.pipe";
import { PriceDetailInBookingType } from "@afidium/ts-facade/model/priceDetailInBookingType";
import { TypeForRateType } from "@afidium/ts-facade/model/typeForRateType";
import { Osi } from "@afidium/ts-facade/model/osi";
import { DifferenceType } from "@afidium/ts-facade/model/differenceType";
import { FareOptimisationDifferencesService } from "../../services/fare-optimisation-differences.service";
import { IDifference } from "libraries/interfaces/fare-optimisation/IFareOptimisation";
import {PriceTotalsContentInBookingType} from "@afidium/ts-facade/model/priceTotalsContentInBookingType";
import {TotalPerTravellerType} from "@afidium/ts-facade/model/totalPerTravellerType";


export class Wording {
    public readonly traveller: string = Yii.t("wui-sasha-js", "Voyageurs : ");
    public readonly vendor: string = Yii.t("wui-sasha-js", "Compagnie émettrice : ");
    public readonly tarification: string = Yii.t("wui-sasha-js", "Tarif : ");
    public readonly validity: string = Yii.t("wui-sasha-js", "Validité (LDTP) : ");
    public readonly source: string = Yii.t("wui-sasha-js", "Source : ");
    public readonly whenToIssue: string = Yii.t("wui-sasha-js", "Émettre avant (LTD) : ");
    public readonly whenIssued: string = Yii.t("wui-sasha-js", "Émis le (LTD) : ");
    public readonly baggage: string = Yii.t("wui-sasha-js", "Bagages : ");
    public readonly included: string = Yii.t("wui-sasha-js", "Inclus");
    public readonly notIncluded: string = Yii.t("wui-sasha-js", "Non inclus");
    public readonly savingPerAdult: string = Yii.t("wui-sasha-js", "Économie par adulte : ");
}

@Component({
    selector: 'tarification-header-transportation',
    templateUrl: './tarification-header-transportation.component.html',
    styles: [`
        table {
            border: none;
            border-collapse: unset;
        }

        .tarification-value {
            font-weight: bold;
            padding-left: .375rem;
        }

        .tarification-value, .tarification-label {
            flex-grow: 1;
            width: 50%;
        }

        .tarification-label {
            text-align: right;
        }

        .tarification-info {
            display: flex;
        }

        .fixed {
            width: 50%;
        }

        tr {
            border: 0;
        }
        
        .savingPerAdult {
            color: blue;
        }
        
        .optimisation-timestamp {
            margin-top: 5px;
            text-align: right;
        }
    `],
    providers: [PricesTableService]
})

export class TarificationHeaderTransportationComponent implements OnInit {

    @Input()
    public booking: BookingType;

    @Input()
    public price: PriceInBookingType;

    @Input()
    public costOrRetail: string = ECostOrRetail.cost;

    @Input()
    public differences: DifferenceType[];

    @Input()
    public savingPrice?: PriceTotalsContentInBookingType;

    @Input()
    public tarificationPrice?: PriceInBookingType;

    @Input()
    public whenLastUpdated?: string;

    public savingsPerAdult: CurrencyUnitTotalType[];

    public readonly dateFormat: string = JSON.parse(wenji.ui.dateFormat).short;

    public wording: Wording = new Wording();
    public travellerSummary: string;
    public vendor: string;
    public tarification: string;
    public validity: string;
    public whenToIssued: string;
    public whenToIssuedTitle: string;
    public hostCode: string;
    public luggageCode: string;
    public luggageInHold: string = "LuggageInHold";
    public noLuggageInHold: string = "NoLuggageInHold";

    public vendorDifference: IDifference;
    public validityDifference: IDifference;
    public travellersDifference: IDifference;
    public tarificationDifference: IDifference;
    public luggageDifference: IDifference;

    public constructor(
        private readonly pricesTableService: PricesTableService,
        private readonly computeTravellerTitle: ComputeTravellerTitlePipe,
        private readonly fareOptimisationDifferencesService: FareOptimisationDifferencesService
    ) {
    }

    public ngOnInit(): void {
        this.handleTravellers();
        this.vendor = this.getVendor();
        this.tarification = this.getTarification();
        this.validity = this.getValidity();
        this.whenToIssued = this.getLTD();
        this.luggageCode = this.getLuggage();
        this.hostCode = this.booking?.host.code;
        if(this.differences){
            this.getAdultSaving();
        }
    }

    public handleTravellers(): void {
        const travellerArray: TravellerType[] = [];
        this.price[this.costOrRetail].detailArray.forEach((detail: PriceDetailInBookingType): void => {
            travellerArray.push(...detail.travellerArray);
        });
        this.travellerSummary = this.getTraveller(travellerArray);
        if(this.differences && this.tarificationPrice){
            const tarificationTravellerArray: TravellerType[] = [];
            this.tarificationPrice[this.costOrRetail].detailArray.forEach((detail: PriceDetailInBookingType): void => {
                tarificationTravellerArray.push(...detail.travellerArray);
            });
            const tarificationTravellerSummary: string = this.getTraveller(tarificationTravellerArray);
            if(this.travellerSummary !== tarificationTravellerSummary){
                this.travellersDifference = {
                    actualValue: this.travellerSummary,
                    otherValue: tarificationTravellerSummary,
                    impact: "Change"
                };
            }
        }
    }

    public getTraveller(travellerArray: TravellerType[]): string {
        const travellersCount: Map<string, number> = this.getTravellerCountByType(travellerArray);
        return this.createTravellersKey(travellersCount);
    }

    private getTravellerCountByType(travellerArray: TravellerType[]): Map<string, number> {
        const travellersCount: Map<string, number> = new Map<string, number>();
        if (travellerArray?.length) {
            travellerArray.forEach((traveller: TravellerType): void => {
                const travellerType: string = this.computeTravellerTitle.getTravellerType(traveller.xType);
                if (travellersCount.has(travellerType)) {
                    travellersCount.set(travellerType, travellersCount.get(travellerType) + 1);
                } else {
                    travellersCount.set(travellerType, 1);
                }
            });
        }
        return travellersCount;
    }

    private createTravellersKey(travellersCount: Map<string, number>): string {
        const travellerArray: string[] = [];
        for (const [travellerType, count] of travellersCount.entries()) {
            travellerArray.push(`${count} ${travellerType}`);
        }
        return travellerArray.join(' - ');
    }

    private getVendor(): string {
        let priceVendor: string = null;
        let isFullName: boolean = false;
        for (const detail of this.price[this.costOrRetail].detailArray) {
            if (detail.rate?.vendor) {
                if(!priceVendor){
                    priceVendor = detail.rate.vendor?.iata ||
                        detail.rate.vendor?.code ||
                        detail.rate.vendor?.carrierArray?.[0]?.iata;
                }

                if(detail.rate?.vendor?.name?.value){
                    priceVendor = detail.rate.vendor.name.value;
                    isFullName = true;
                    break;
                }
            }
            // We're looking for vendor, not carrier
            /*if (detail.rate?.fareComponentArray?.length && (!isFullName || !priceVendor)) {
                for (const element of detail.rate?.fareComponentArray) {
                    if(element.carrier.nameArray[0].value){
                        priceVendor = element.carrier.nameArray[0].value;
                        isFullName = true;
                        break;
                    }
                }
            }*/
        }
        if (this.booking?.osiArray?.length && (!isFullName || !priceVendor)) {
            this.booking.osiArray?.forEach((osi: Osi): void => {
                    if (osi.vendor?.name?.value || osi.code || osi.iata || osi.vendor?.carrierArray?.[0]?.iata) {
                        priceVendor = osi.vendor?.name?.value || osi.code || osi.iata || osi.vendor?.carrierArray?.[0]?.iata;
                    }
                }
            );
        }
        if (this.differences) {
            this.vendorDifference = this.fareOptimisationDifferencesService.getVendorDifference(this.differences, priceVendor);
        }
        return priceVendor;
    }

    private getTarification(): string {
        let data: string;
        for (const detail of this.price[this.costOrRetail].detailArray) {
            if (detail.rate && detail.rate?.typeArray) {
                const typeWithXRole: TypeForRateType = detail.rate?.typeArray?.find((type: TypeForRateType): boolean => !!type.xRole);
                if (typeWithXRole) {
                    data = typeWithXRole?.xRole;
                    break;
                }
            }
        }

        if (this.differences) {
            this.tarificationDifference = this.fareOptimisationDifferencesService.getTarificationDifference(this.differences, data);
        }
        return data;
    }


    private getValidity(): string {
        const priceValidity: string = this.price[this.costOrRetail].detailArray?.find((detail: PriceDetailInBookingType): string =>
            detail.rate &&
            detail.rate?.validity?.max?.value
        )?.rate?.validity?.max?.value;

        if (this.differences) {
            this.validityDifference = this.fareOptimisationDifferencesService.getValidityDifference(this.differences, priceValidity);
        }
        return priceValidity;
    }

    private getLTD(): string {
        this.whenToIssuedTitle = this.booking?.whenIssued ? this.wording.whenIssued : this.wording.whenToIssue;
        if (this.differences) {
            this.whenToIssuedTitle = '';
            return null;
        }
        return this.booking?.whenIssued?.date || this.booking?.whenToIssue?.date;
    }

    private getLuggage(): string {
        const detailElement: PriceDetailInBookingType = this.price[this.costOrRetail].detailArray?.find((detail: PriceDetailInBookingType): KeywordType =>
            detail.rate &&
            detail.rate?.keywordArray?.length &&
            detail.rate?.keywordArray?.find((keyWord: KeywordType): boolean => keyWord.xCode === this.luggageInHold)
        );
        const data: string = detailElement ? this.wording.included : this.wording.notIncluded;
        if (this.differences) {
            this.luggageDifference = this.fareOptimisationDifferencesService.getLuggageDifference(this.differences, data, this.wording.notIncluded, this.wording.included);
        }
        return data;
    }

    private getAdultSaving(): void {
        let element: PriceItemType = this.savingPrice.totalArray.find((total: PriceItemType): boolean => !total.xReason);
        if(!element){
            element = this.savingPrice.totalArray[0];
        }
        const adult: TotalPerTravellerType = element.totalPerTravellerArray.find((totalPerTraveller: TotalPerTravellerType): boolean => totalPerTraveller.traveller.xType === "Adult");
        if(adult){
            this.savingsPerAdult = adult.total.currencyArray;
        }
    }
}
